import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import InventoryIcon from '@mui/icons-material/Inventory';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const StockJournalEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, permissions, token, logout } = useAuth();
  const branchId = 0;

  const [isLoading, setIsLoading]           = useState(false);
  const [errorMessage, setErrorMessage]     = useState('');
  const [errorMessageModel, setErrorMessageModel] = useState('');
  const [itemOptions, setItemOptions]       = useState([]);
  const [selectedItemOption, setSelectedItemOption] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [data, setData]                     = useState({});
  const [modalData, setModalData]           = useState({ ItemName: '', ItemId: '', Quantity: '1' });
  const [storeLocationOptions, setStoreLocationOptions] = useState([]);
  const [selectedStoreLocation, setSelectedStoreLocation] = useState(null);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
    fetchStoreLocations();
  }, []);

  const fetchStoreLocations = () => {
    fetchData('StoreLocationApi', `/${companyId}`, token)
      .then((res) => {
        if (res.responseCode === '1111') setStoreLocationOptions(res.response_data);
      })
      .catch((err) => console.error('Error fetching store locations:', err));
  };

  const fetchTableData = async () => {
    try {
      const res = await fetchData('StockJournalApi', `/${id}`, token);
      if (res.responseCode === '1111') {
        setData(res.response_data.StockJournal);
        setInvoiceDetails(res.response_data.StockJournalDetails);
        if (res.response_data.StockJournal.StoreLocationId) {
          setSelectedStoreLocation({
            value: res.response_data.StockJournal.StoreLocationId,
            label: res.response_data.StockJournal.StoreLocationName,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching stock journal:', err);
      if (err.response?.status === 401) { logout(); history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); }
    }
  };

  const fetchItemOptions = async (inputValue) => {
    if (inputValue.length <= 2) return [];
    try {
      const res = await fetchData('StockJournalApi', `/Item/${companyId}/${branchId}/${inputValue}`, token);
      if (res.responseCode === '1111' && Array.isArray(res.response_data)) {
        setItemOptions(res.response_data);
        return res.response_data.map(item => ({ value: item.Id, label: `${item.ItemName} (${item.BalanceStock})` }));
      }
    } catch (err) { console.error('Error fetching items:', err); }
    return [];
  };

  const handleChangeDrop = (selected) => {
    setSelectedItemOption(selected);
    if (!selected) return;
    const detail = itemOptions.find(i => i.Id === selected.value);
    setModalData({ ItemName: detail?.ItemName || '', ItemId: selected.value, Quantity: '1' });
  };

  const saveModalData = () => {
    if (!modalData.ItemId) { setErrorMessageModel('Item is required'); return; }
    if (!modalData.Quantity || isNaN(modalData.Quantity)) { setErrorMessageModel('Quantity must be a valid number'); return; }
    if (invoiceDetails.some(i => i.ItemId === modalData.ItemId)) { alert('This item is already added.'); return; }
    setErrorMessageModel('');
    setInvoiceDetails(prev => [modalData, ...prev]);
    setModalData({ ItemName: '', ItemId: '', Quantity: '1' });
    setSelectedItemOption(null);
  };

  const handleCalculation = (e, index) => {
    const { name, value } = e.target;
    setInvoiceDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const removeRecord = (index) => {
    if (window.confirm('Are you sure you want to remove this item?')) {
      setInvoiceDetails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (!data.BillNumber) { setErrorMessage('Bill Number is required!'); setIsLoading(false); return; }
    const payload = {
      StockJournal: { Id: id, BranchId: branchId, CompanyId: companyId, Remarks: data.Remarks, BillDate: data.BillDate, BillNumber: data.BillNumber, CreatedBy: data.CreatedBy, FinYear: data.FinYear, IsActive: data.IsActive, UpdatedBy: userId, StoreLocationId: selectedStoreLocation?.value ?? null },
      StockJournalDetails: invoiceDetails,
    };
    updateData('StockJournalApi', `${id}`, payload, token)
      .then(() => history.push('/StockJournalIndex'))
      .catch(err => { console.error('Error updating:', err); setIsLoading(false); });
  };

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <InventoryIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Stock Journal Edit</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubmit} disabled={isLoading}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
              {isLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : '✓ Update'}
            </button>
            <Link to="/StockJournalIndex" onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {errorMessage && (
        <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>
          {errorMessage}
        </div>
      )}

      {/* ── Bill Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Bill Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Bill Date <span style={{ color: C.danger }}>*</span></label>
            <input type="date" name="BillDate" value={formatDateForInput(data.BillDate)} onChange={handleChange} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div>
            <label style={lbl}>Bill No <span style={{ color: C.danger }}>*</span></label>
            <input type="text" value={`${data.BillPrefix || ''}${data.BillNumber || ''}/${data.FinYear || ''}`} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div>
            <label style={lbl}>Store Location</label>
            <Select
              options={storeLocationOptions.map((s) => ({ value: s.Id, label: s.Name }))}
              value={selectedStoreLocation}
              onChange={(selected) => setSelectedStoreLocation(selected)}
              isClearable
              isSearchable
              placeholder="Store Location"
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
                valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
                indicatorsContainer: (base) => ({ ...base, height: '28px' }),
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                menu: (base) => ({ ...base, fontSize: '12px' }),
              }}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Remarks</label>
            <input type="text" name="Remarks" value={data.Remarks || ''} onChange={handleChange} placeholder="Remarks" style={inp} />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '10px' }}>

        {/* ── Items table ── */}
        <div style={card}>
          <div style={cardHead('#1F2A38')}>📦 Items ({invoiceDetails.length})</div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ backgroundColor: '#1F2A38', color: '#fff', position: 'sticky', top: 0 }}>
                <tr>
                  {['#', 'Item Name', 'Qty', ''].map((h, i) => (
                    <th key={i} style={{ padding: '6px 8px', textAlign: i >= 2 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceDetails.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: C.muted, fontSize: '12px' }}>No items added yet</td></tr>
                ) : invoiceDetails.map((item, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${C.cardBorder}` }}>
                    <td style={{ padding: '3px 8px', color: C.muted, fontSize: '11px' }}>{index + 1}</td>
                    <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.ItemName}</td>
                    <td style={{ padding: '2px 4px', width: '90px' }}>
                      <input type="number" value={item.Quantity} name="Quantity"
                        onChange={e => handleCalculation(e, index)}
                        style={{ ...inp, height: '24px', borderRadius: '4px', textAlign: 'right' }} />
                    </td>
                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                      <button onClick={() => removeRecord(index)}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: C.danger, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <DeleteIcon style={{ fontSize: 15 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Add Item panel ── */}
        <div style={card}>
          <div style={cardHead('#f59e0b')}>➕ Add Item</div>
          <div style={cardBody}>
            {errorMessageModel && (
              <div style={{ marginBottom: '8px', padding: '5px 10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '11px', color: C.danger }}>
                {errorMessageModel}
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              <label style={lbl}>Item Name</label>
              <AsyncSelect
                cacheOptions
                loadOptions={fetchItemOptions}
                onChange={handleChangeDrop}
                value={selectedItemOption}
                isSearchable
                placeholder="Type to search item…"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
                  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
                  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, fontSize: '12px' }),
                }}
              />
            </div>
            <button onClick={saveModalData}
              style={{ width: '100%', height: '30px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add to List
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StockJournalEdit;
